import React, { useEffect, useState } from 'react'
import { StoreApi } from '../../api/storeApi'
import { SsoMicrosoftApi } from '../../api/ssoMicrosoftApi'
import useTranslation from '../../hooks/useTranslation'
import { setErrorNotification, setSuccessNotification } from '../../actions/notificationAction'
import { useDispatch } from 'react-redux'
import AuthStorage from '../../services/authStorage'
import { AuthSettings, SsoSettings } from '../../shared-components'
import useMicrosoftConnect from './useMicrosoftConnect'

const Auth = new AuthStorage()

const AuthSettingsContainer = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { connectMicrosoft } = useMicrosoftConnect()

  const [store, setStore] = useState({
    enable_account_registration: false,
    auto_account_approval: false,
    password_protection: 0,
    authentication: 0
  })

  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getStoreAuths()

    const url = new URL(window.location.href)
    const microsoftStatus = url.searchParams.get('microsoft')
    if (microsoftStatus === 'connected') {
      dispatch(setSuccessNotification({ message: 'Microsoft SSO Connected!' }))
    } else if (microsoftStatus === 'failed') {
      dispatch(setErrorNotification({ message: 'Microsoft SSO Failed.' }))
    }
    if (microsoftStatus) {
      url.searchParams.delete('microsoft')
      window.history.replaceState({}, document.title, url.toString())
    }
  }, [])

  const getStoreAuths = async () => {
    try {
      const { data } = await StoreApi.getStoreAuthWithConfig()
      setStore(
        {...data,
          password_protection: Number(data.password_protection) || 0,
          authentication: Number(data.authentication) || 0
        })
    } catch (e) {
      console.log(e)
    }
  }

  const onChange = (event) => {
    const { name, value, type, checked } = event.target

    let updatedValue =
      type === 'checkbox'
        ? checked
        : (name === 'authentication' || name === 'password_protection')
          ? Number(value)
          : value

    let updatedStore = {...store, [name]: updatedValue}

    if (name === 'authentication' && (Number(value) === 0 || Number(value) === 3)) {
      updatedStore.enable_account_registration = false
    }

    setStore(updatedStore)
  }

  const saveStoreSettings = async (event) => {
    event.preventDefault()
    setSaving(true)

    try {
      const { data } = await StoreApi.updateStore(store)
      Auth.setStore({...store, ...data})
      dispatch(setSuccessNotification({ message: t('response_messages.store_updated_successfully') }))
    }
    catch(error) {
      dispatch(setErrorNotification({ message: error.response.data }))
    }
    setSaving(false)
  }

  const handleSsoConnect = async () => {
    try {
      await connectMicrosoft()
    } catch (e) {
      dispatch(setErrorNotification({ message: e?.response?.data || 'Microsoft SSO connect failed.' }))
      throw e
    }
  }

  const handleSsoDisconnect = async (id) => {
    try {
      await SsoMicrosoftApi.disconnectMicrosoft(id)
      dispatch(setSuccessNotification({ message: 'Microsoft SSO disconnected successfully' }))

      if (store.authentication === 3) {
        await StoreApi.updateStore({ ...store, authentication: 2 })
      }

      await getStoreAuths()
    } catch (e) {
      dispatch(setErrorNotification({ message: e?.response?.data || 'Microsoft SSO disconnect failed.' }))
      throw e
    }
  }

  const handleSsoToggleEnabled = async (config) => {
    try {
      await SsoMicrosoftApi.toggleMicrosoftSso(config)
      dispatch(setSuccessNotification({ message: `Microsoft SSO ${config.enabled ? 'enabled' : 'disabled'} successfully` }))

      if (!config.enabled && store.authentication === 3) {
        await StoreApi.updateStore({ ...store, authentication: 2 })
      }

      await getStoreAuths()
    } catch (e) {
      dispatch(setErrorNotification({ message: e?.response?.data || 'Failed to update Microsoft SSO.' }))
      throw e
    }
  }

  const micConfig = (store.ssoConfigs || []).find(c => c.provider === 'microsoft' && c.tenant_id)

  return (
    <AuthSettings
      store={store}
      onChange={onChange}
      onSave={saveStoreSettings}
      saving={saving}
      ssoContent={
        <SsoSettings
          micConfig={micConfig}
          onConnect={handleSsoConnect}
          onDisconnect={handleSsoDisconnect}
          onToggleEnabled={handleSsoToggleEnabled}
        />
      }
    />
  )
}

export default AuthSettingsContainer
